import { Input } from '@material-ui/core';
import { Autocomplete, AutocompleteInputChangeReason } from '@material-ui/lab';
import classNames from 'classnames';
import { debounce, omit, startCase, toLower } from 'lodash';
import { FC, HTMLAttributes, forwardRef, useMemo, useState } from 'react';

import { IconCircleCrossFilled } from 'src/general/Icons/IconCircleCrossFilled';
import { IconLocationPin } from 'src/general/Icons/IconLocationPin';
import {
  LocationObject,
  LocationResponse,
} from 'src/general/components/SetLocationPopup/EnterLocationScreen/SetLocation/SetLocation.types';

import classes from './FormikInput.module.scss';
import { FormikInputBaseProps, FormikInputWrapperChildProps } from './FormikInput.types';
import { FormikInputWrapper } from './FormikInputWrapper';

const DEBOUNCE_TIME = 1000;

interface FormikInputLocationProps extends Pick<FormikInputBaseProps, 'name' | 'label' | 'helpText'> {
  placeholder?: string;
  onFocus?: () => void;
  onLocationChange?: (value: LocationObject | null) => void;
  searchLocation: (userInput: string) => Promise<LocationResponse>;
  testIdPrefix?: string;
}

export const FormikInputLocation: FC<FormikInputLocationProps> = ({ placeholder, ...rest }) => (
  <FormikInputWrapper
    className={classNames(classes.location, {
      [classes.labelOutside]: !!placeholder,
    })}
    {...rest}
    placeholder={placeholder}
    Component={FormikInputLocationInner as any}
  />
);

interface FormikInputLocationInnerProps
  extends FormikInputWrapperChildProps<string>,
    Omit<FormikInputLocationProps, 'name' | 'label' | 'helpText'> {}

const FormikInputLocationInner: FC<FormikInputLocationInnerProps> = ({
  field,
  placeholder,
  searchLocation,
  onFocus,
  onLocationChange,
  testIdPrefix = 'FormikInputLocation',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<LocationResponse>([]);

  const value = useMemo(() => getLocationObject(field.value), [field.value]);

  const onInputChange = useMemo(
    () =>
      debounce((_e, inputValue: string, reason: AutocompleteInputChangeReason) => {
        if (reason === 'reset' || inputValue.length < 3) {
          setOpen(false);
          setOptions([]);
        } else {
          setOpen(true);
          setLoading(true);
          searchLocation(inputValue)
            .then(setOptions)
            .catch(() => {})
            .finally(() => setLoading(false));
        }
      }, DEBOUNCE_TIME),
    [searchLocation],
  );

  return (
    <Autocomplete
      // If an ID prop is not provided, a randomly generated one will be used
      // which makes snapshot testing impossible.
      data-testid={`${testIdPrefix}-search`}
      fullWidth
      autoHighlight
      selectOnFocus
      value={value}
      open={open}
      loading={loading}
      popupIcon=""
      ListboxComponent={Listbox}
      ListboxProps={{ testIdPrefix }}
      closeIcon={<IconCircleCrossFilled className={classes.locationIcon} />}
      options={options}
      getOptionLabel={getLocationString}
      onFocus={onFocus}
      onClose={() => setOpen(false)}
      onInputChange={onInputChange}
      onChange={(event, location) => {
        field.onChange({
          ...event,
          target: {
            ...event.target,
            name: field.name,
            value: location ? getLocationString(location) : '',
          },
        });
        onLocationChange?.(location);
      }}
      renderOption={(option) => (
        <div className={classes.locationOption} data-testid={`${testIdPrefix}-option`}>
          <IconLocationPin className={classes.locationIcon} />
          {getLocationString(option)}
        </div>
      )}
      renderInput={({
        id,
        InputLabelProps,
        InputProps: { startAdornment, endAdornment, ...InputProps },
        inputProps,
        ...rest
      }) => (
        <Input
          type="text"
          name={field.name}
          placeholder={placeholder}
          inputProps={omit(inputProps, 'id', 'className')}
          startAdornment={startAdornment as any}
          endAdornment={endAdornment as any}
          {...InputProps}
          {...rest}
        />
      )}
    />
  );
};

type ListBoxProps = HTMLAttributes<HTMLUListElement> & Pick<FormikInputLocationProps, 'testIdPrefix'>;

const Listbox = forwardRef<HTMLUListElement, ListBoxProps>(({ className, testIdPrefix, children, ...rest }, ref) => (
  <ul ref={ref} data-testid={`${testIdPrefix}-listbox`} className={classNames(className, classes.listbox)} {...rest}>
    {children}
  </ul>
));

Listbox.displayName = 'Listbox';

// eslint-disable-next-line camelcase
const getLocationString = ({ name, post_code, state }: LocationObject) =>
  // eslint-disable-next-line camelcase
  `${startCase(toLower(name))} ${post_code} ${state}`;

/**
 * In the case that FormikInputLocation is passed an initial string value, we need to create a dummy location object to
 * pass to the underlying Autocomplete input.
 *
 * Note:
 * getLocationString(getLocationObject(locString)) === locString, however:
 * getLocationObject(getLocationString(locObject)) !== locObject as we lose the values of 'can_deliver' & 'can_pickup'
 * (as well as referential equality).
 */
const getLocationObject = (locationString?: string): LocationObject | null => {
  if (!locationString) return null;

  // eslint-disable-next-line camelcase
  const [name, post_code, state] = locationString.replace(/^(.*) (\w*) (\w*)$/, `$1_$2_$3`).split('_');

  return {
    name,
    state,
    // eslint-disable-next-line camelcase
    post_code,
    // eslint-disable-next-line camelcase
    can_deliver: false,
    // eslint-disable-next-line camelcase
    can_pickup: false,
    // eslint-disable-next-line camelcase
    pickup_address: null,
  };
};
